/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    IQueryParams,
    PFWmsEntities,
    ProfileProvider,
    SafeAny,
} from '@pf/shared-common';
import {
    ReplenishmentCreate,
    ReplenishmentDto,
    ReplenishmentRead,
    ReplenishmentSearchParams,
} from '../entities/replenishment.dto';
import { ReplenishmentDataService } from '../infrastructure/replenishments/replenishment.data.service';
import { ReplenishmentStore } from '../infrastructure/replenishments/replenishment.store';
import { ReplenishmentMapper } from '../infrastructure/replenishments/replenishment.mapper';
import {
    ReplenishmentCompleteViewModel,
    ReplenishmentCompleteResultViewModel,
    ReplenishmentStatus,
    ResourceAuth,
} from '@control-tower/platform-wms';
import {
    catchError,
    filter,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
} from 'rxjs';
import { WarehouseFacade } from './warehouse.facade';
import { WarehouseDto } from '../entities/warehouse.dto';
import { isValue } from '@pf/shared-utility';

@Injectable()
export class ReplenishmentFacade extends AbstractManageEntityFacade<
    ReplenishmentDto,
    ReplenishmentRead,
    ReplenishmentCreate,
    ReplenishmentSearchParams
> {
    nameField: keyof ReplenishmentDto = 'id';

    constructor(
        dataService: ReplenishmentDataService,
        store: ReplenishmentStore,
        mapper: ReplenishmentMapper
    ) {
        super(dataService, null, store, mapper);
        this.listenForRealTimeEvents(PFWmsEntities.Replenishment, {
            processEventsIfNotInStore: true,
            customEntityMapping: (entity: ReplenishmentRead) => {
                return dataService.get$(entity.id, true);
            },
        });
    }

    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly profileProvider = inject(ProfileProvider);

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ReplenishmentDto, string>> {
        return { id: searchText };
    }

    protected override getDefaultSearchParams$(
        params: IQueryParams<ReplenishmentDto>
    ): Observable<IQueryParams<ReplenishmentDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ReplenishmentCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    pendingReplenishments$() {
        return this.loadReplenishments$(ReplenishmentStatus.Pending, false);
    }

    activeReplenishments$() {
        return this.loadReplenishments$(ReplenishmentStatus.Active, true);
    }

    private loadReplenishments$(
        status: ReplenishmentStatus,
        currentUserOnly: boolean
    ) {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    status: status,
                },
            },
            false,
            100
        ).pipe(
            map(() => true),
            switchMap(() =>
                this.observeReplenishments$(status, currentUserOnly)
            ),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private observeReplenishments$(
        status: ReplenishmentStatus,
        currentUserOnly: boolean
    ) {
        return this.warehouseFacade.active$.pipe(
            filter(isValue),
            switchMap((warehouse: WarehouseDto) => {
                return this.store.filter$(
                    (replenishment: ReplenishmentRead) => {
                        return (
                            !replenishment.isDeleted &&
                            replenishment.warehouse?.id === warehouse.id &&
                            replenishment.status === status &&
                            (currentUserOnly
                                ? replenishment.assignedToUser?.id ===
                                  this.profileProvider.profile.userId
                                : true)
                        );
                    }
                );
            }),
            map((replenishments: ReplenishmentRead[]) =>
                this.mapper.toDTOList(replenishments)
            )
        );
    }

    completeReplenishment$(
        replenishmentId: string,
        replenishmentCompleteViewModel: ReplenishmentCompleteViewModel
    ): Observable<ReplenishmentCompleteResultViewModel> {
        return (
            this.dataService as ReplenishmentDataService
        ).completeReplenishment$(
            replenishmentId,
            replenishmentCompleteViewModel
        );
    }

    cancelReplenishment$(id: string): Observable<ReplenishmentRead> {
        return (
            this.dataService as ReplenishmentDataService
        ).cancelReplenishment$(id);
    }
}
